import { Collapse, ConfigProvider, Flex, Modal, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { bookingsByCustomerPackageV2Options } from "hooks/queries/booking-v2";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { BookingDto } from "shared/types";
import { BookingStatus } from "shared/types/enum";
import { getBookingStatusLabel, groupByDate } from "shared/utils";
import { localDateOnly, localTzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

// The statuses that are still LIVE on a shared package: they hold a credit and
// the class has not been settled. Completed/NoShow/Cancelled are history and
// would only pad the list with rows the admin cannot act on.
const LIVE_STATUSES: string[] = [BookingStatus.Confirmed, BookingStatus.Waitlist];

type LiveBooking = BookingDto & { occurrence_timestamp: string };

const BookingCard = ({ booking }: { booking: LiveBooking }) => {
  const start = localTzDate(booking.occurrence_timestamp);
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${colors.lineStrong}`,
        borderRadius: 12,
        background: colors.card,
      }}
    >
      <Text style={{ fontWeight: 500 }}>
        {booking.class_name ?? translate("class")}
      </Text>
      <div>
        <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
          {start.format("HH:mm")}
        </Text>
        <Text type="secondary" style={{ fontSize: textPx(12.5) }}>
          {" "}
          · {getBookingStatusLabel(booking.booking_status)}
        </Text>
      </div>
    </div>
  );
};

// Date + count, used as the Collapse panel label. Everything starts collapsed:
// the header alone already answers "when, and how many", which is the decision
// the admin is making, and a member with classes across several days would
// otherwise push the buttons off the modal.
const DayHeader = ({ date, count }: { date: string; count: number }) => (
  // Byte-for-byte the WorkingHoursConflictWarningModal DayGroupHeader: the
  // count is nested INSIDE the date Text (not a sibling in a Flex), so the two
  // sit on one baseline at the same size, with weight alone separating them.
  <Text style={{ fontWeight: 600 }}>
    {localDateOnly(date).format("dddd, DD MMMM YYYY")}
    <Text style={{ color: colors.ink3, fontWeight: 500, marginLeft: 6 }}>
      · {count} {translate("leave.conflict.classCount")}
    </Text>
  </Text>
);

const DayBody = ({ bookings }: { bookings: LiveBooking[] }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {bookings.map((booking) => (
      <BookingCard key={booking.id} booking={booking} />
    ))}
  </div>
);

/**
 * Remove-access confirmation, showing exactly what the removal will and will
 * not touch. The generic wording ("classes they have already booked stay") is
 * true but abstract; naming the actual classes turns it into a decision the
 * admin can make with their eyes open, and the no-classes case is just as
 * useful — it says the removal is clean.
 */
const RemovePackageMemberModal = ({
  open,
  memberName,
  memberUserId,
  customerPackageId,
  packageName,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  memberName: string;
  memberUserId: string;
  customerPackageId: string;
  packageName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  // Only fetch while the confirmation is actually up. This component renders
  // alongside the sharing button on every package detail page, so an
  // unconditional query would fire on page load for a modal nobody opened.
  const { data, isError } = useQuery(
    bookingsByCustomerPackageV2Options(open ? customerPackageId : undefined),
  );
  // Derived from the data itself rather than isLoading: a DISABLED query
  // reports isLoading false with data undefined, which would render the
  // "no classes booked" line as though the check had run and come back empty.
  // "We have rows" is the only safe basis for making that claim.
  const checked = data !== undefined;

  const live = useMemo<LiveBooking[]>(
    () =>
      (data ?? [])
        .filter(
          (booking) =>
            booking.customer_id === memberUserId &&
            LIVE_STATUSES.includes(booking.booking_status) &&
            // schedule_date is optional on the DTO; a row without one cannot be
            // placed on a day, and grouping would throw on an invalid date.
            !!booking.schedule_date,
        )
        .map((booking) => ({
          ...booking,
          occurrence_timestamp: booking.schedule_date as string,
        })),
    [data, memberUserId],
  );

  const dayGroups = useMemo(() => groupByDate(live), [live]);

  // Never claim "no classes" from an empty array that simply has not loaded, or
  // from a request that failed — that would be a confident lie about the thing
  // the admin opened this modal to check.
  const summary = isError
    ? translate("package_sharing.remove_check_failed")
    : !checked
      ? translate("package_sharing.remove_checking")
      : live.length === 0
        ? translate("package_sharing.remove_no_bookings").replace(
            "{{name}}",
            memberName,
          )
        : translate("package_sharing.remove_has_bookings")
            .replace("{{name}}", memberName)
            .replace("{{count}}", String(live.length));

  return (
    <ConfigProvider theme={modalButtonsTheme}>
      <Modal
        open={open}
        title={
          <ModalHeader
            tone="amber"
            title={translate("package_sharing.remove_confirm_title")}
            eyebrow={packageName}
          />
        }
        okText={translate("package_sharing.remove_client")}
        cancelText={translate("button.cancel")}
        okButtonProps={{ danger: true, loading: !checked && !isError }}
        onOk={onConfirm}
        onCancel={onCancel}
        width={480}
        destroyOnClose
        // A member can hold many classes; cap the body in its own scroll region
        // so the title and footer stay reachable.
        styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
      >
        <Text style={modalSubtitleStyle}>{summary}</Text>
        {live.length > 0 && (
          <>
            <Flex style={{ marginTop: 16 }}>
              <Text style={{ fontSize: textPx(13), color: colors.ink2 }}>
                {/* Credits, not classes: a class can take more than one
                    (booking_v2.credit_cost). */}
                {translate("package_sharing.remove_bookings_note").replace(
                  "{{count}}",
                  String(live.reduce((sum, booking) => sum + (booking.credit_cost ?? 1), 0)),
                )}
              </Text>
            </Flex>
            <Collapse
              ghost
              style={{ marginTop: 8 }}
              items={dayGroups.map(([date, bookings]) => ({
                key: date,
                label: <DayHeader date={date} count={bookings.length} />,
                children: <DayBody bookings={bookings} />,
              }))}
            />
          </>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default RemovePackageMemberModal;
